import { CustomButton } from "@/components/custom/custom-button";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { changeRoleSchema } from "@/schemas/user.schema";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  styled,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 500,
    width: "100%",
  },
}));

const transformPayload = (values) => ({
  roleName: values.roleName,
  description: values.description,
});

const NewRoleDialog = ({ open, onClose, mutationFn }) => {
  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      roleName: "",
      description: "",
    },
    validationSchema: changeRoleSchema,
    onSubmit: (values) => {
      mutation.mutate(transformPayload(values));
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => mutationFn(payload),
    onSuccess: () => {
      formik.resetForm();
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      onClose();
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Create New Admin Role
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Fill in the details below to add a new role.
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormTextInput formik={formik} name="roleName" label="Role Name" placeholder="e.g. Role System Admin" />
          <FormTextInput
            formik={formik}
            name="description"
            label="Description"
          />
        </Stack>

        {mutation.isError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {mutation.error?.message ??
              "Failed to create admin role. Try again."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 2, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Creating..." : "Create Role"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default NewRoleDialog;
